// Budget allocation for a payment request: PO → OR line, with the account /
// analysis codes and the center × category budget bucket each line draws on.
// A saved PR reads GET /payment-requests/{id}/budget-allocations; the create
// form previews from GET /budgets/allocation-matrix with the typed per-line
// values on top (create/lineAllocation.js). Budget and PO figures repeat per
// row, so only the line amounts and
// `value` are summed. `orLineAmount` is what the OR requested; `poLineAmount`
// is the line at the PO's price.

const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v))

const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const BUDGET_IMPACT_LABELS = {
  PREVIEW: "after submit",
  COMMITTED: "reserved",
  RELEASED: "released",
}

export const VALIDATION_LABELS = {
  LEGACY_MULTI_PO_OR_LINE: "OR line shared by several POs",
  PO_MISSING_OR_LINE_MAPPING: "PO has no OR line mapping",
  CENTER_NAME_NOT_FOUND: "Center not found",
  BUDGET_CENTER_UNMAPPED: "Budget center not mapped",
  BUDGET_CATEGORY_MISSING: "No budget category",
  BUDGET_BUCKET_NOT_FOUND: "No budget for this month",
  INSUFFICIENT_BUDGET: "Exceeds remaining budget",
  CENTER_NOT_FOUND: "Center not found",
  CENTER_UNMAPPED: "Budget center not mapped",
  CATEGORY_NOT_FOUND: "Budget category not found",
}

export const analysisLabel = (row) =>
  LEVELS.filter((n) => row[`l${n}Code`])
    .map((n) => `L${n} ${row[`l${n}Code`]}`)
    .join(" · ")

const BUCKET_HINT = "Per center × category budget bucket, not per line"

// LinePeriod: periodKey "2026-09" | "UNALLOCATED" (months finance hasn't funded yet).
const UNALLOCATED = "UNALLOCATED"

export const allocationMonthLabel = (r) =>
  r.periodLabel || (r.month ? `${String(r.month).padStart(2, "0")}/${r.year}` : "")

const idList = (v) =>
  (Array.isArray(v) ? v : String(v ?? "").split(","))
    .map((x) => String(x).trim())
    .filter(Boolean)

export const BUDGET_ALLOCATION_COLUMNS = [
  { key: "monthLabel", label: "Month" },
  { key: "poCode", label: "PO" },
  { key: "orCode", label: "OR" },
  { key: "budgetCenterName", label: "Center" },
  { key: "departmentName", label: "Department" },
  { key: "categoryName", label: "Category" },
  { key: "itemGroupName", label: "Item group" },
  { key: "itemName", label: "Item" },
  { key: "accountCode", label: "Account code" },
  { key: "analysis", label: "Analysis code" },
  { key: "poLineAmount", label: "PO line amount", money: true },
  { key: "value", label: "This payment", money: true },
  { key: "lineUnpaid", label: "Line unpaid", money: true },
  { key: "linePaid", label: "Line paid", money: true },
  { key: "budget", label: "Bucket budget", money: true, hint: BUCKET_HINT },
  { key: "remaining", label: "Bucket remaining", money: true, hint: BUCKET_HINT },
  { key: "remainingAfter", label: "Remaining after", money: true, hint: BUCKET_HINT },
]

const EXCEL_COLUMNS = [
  { key: "month", label: "Month", width: 8 },
  { key: "year", label: "Year", width: 8 },
  { key: "poCode", label: "PO code", width: 16 },
  { key: "poStatus", label: "PO status", width: 12 },
  { key: "orCode", label: "OR code", width: 14 },
  { key: "sunCenterCode", label: "Center code", width: 14 },
  { key: "centerName", label: "Center", width: 20 },
  { key: "budgetCenterName", label: "Budget center", width: 20 },
  { key: "departmentName", label: "Department", width: 20 },
  { key: "categoryName", label: "Category", width: 20 },
  { key: "itemGroupCode", label: "Item group code", width: 14 },
  { key: "itemGroupName", label: "Item group", width: 22 },
  { key: "lineType", label: "Line type", width: 10 },
  { key: "extraType", label: "Extra type", width: 14 },
  { key: "itemName", label: "Item", width: 30 },
  { key: "description", label: "Description", width: 24 },
  { key: "quantity", label: "Quantity", width: 10 },
  { key: "accountCode", label: "Account code", width: 14 },
  ...LEVELS.map((n) => ({ key: `l${n}Code`, label: `L${n}`, width: 14 })),
  { key: "orLineAmount", label: "OR line amount", width: 16, money: true },
  { key: "poLineAmount", label: "PO line amount", width: 16, money: true },
  { key: "value", label: "This payment", width: 16, money: true },
  { key: "poTotalAmount", label: "PO total", width: 16, money: true },
  { key: "poPaidAmount", label: "PO paid", width: 16, money: true },
  { key: "poPaymentPercentage", label: "% this payment", width: 14, percent: true },
  { key: "poPaidPercentage", label: "% paid", width: 10, percent: true },
  { key: "lineUnpaid", label: "Line unpaid", width: 16, money: true },
  { key: "linePaid", label: "Line paid", width: 16, money: true },
  { key: "budget", label: "Bucket budget", width: 16, money: true },
  { key: "remaining", label: "Bucket remaining", width: 16, money: true },
  { key: "remainingAfter", label: "Remaining after", width: 16, money: true },
  { key: "budgetImpact", label: "Budget impact", width: 14 },
  { key: "validationStatus", label: "Validation status", width: 26 },
]

export const fromMatrixRow = (r) => ({
  key: `${r.purchaseId}:${r.orLineId ?? "po"}`,
  purchaseId: String(r.purchaseId),
  poCode: r.poCode || "",
  poStatus: r.poStatus || "",
  orCode: r.orCode || "",
  orLineId: r.orLineId == null ? null : String(r.orLineId),
  lineType: "ITEM",
  extraType: "",
  description: "",
  month: num(r.fiscalMonth),
  year: num(r.fiscalYear),
  itemId: r.itemId == null ? null : String(r.itemId),
  periodKey: r.periodKey || "",
  periodLabel: r.periodLabel || "",
  budgetStatus: r.budgetStatus || "",
  unallocatedMonths: r.unallocatedMonths || "",
  centerId: r.centerId,
  sunCenterCode: r.sunCenterCode || "",
  centerName: r.centerName || "",
  budgetCenterId: r.budgetCenterId,
  budgetCenterName: r.budgetCenterName || "",
  budgetCategoryId: r.budgetCategoryId,
  departmentName: r.departmentName || "",
  categoryName: r.categoryName || "",
  itemGroupCode: r.itemGroupCode || "",
  itemGroupName: r.itemGroupName || "",
  itemName: r.itemName || "",
  quantity: num(r.quantity),
  accountCode: r.accountCode || "",
  ...Object.fromEntries(LEVELS.map((n) => [`l${n}Code`, r[`l${n}Code`] || ""])),
  orLineAmount: num(r.orLineRequestedReferenceAmount),
  poLineAmount: num(r.poLineAmount),
  value: num(r.prValueAllocatedToItem),
  lineUnpaid: null,
  linePaid: null,
  budget: num(r.budgetBucketTotalReference),
  remaining: num(r.budgetBucketRemainingReference),
  remainingAfter: num(r.budgetBucketRemainingReference),
  budgetImpact: "",
  poPaymentAmount: null,
  poTotalAmount: null,
  poPaidAmount: null,
  poPaymentPercentage: null,
  poPaidPercentage: null,
  validationStatus: r.validationStatus || "",
})

export const EXTRA_TYPE_LABELS = {
  DELIVERY_FEE: "Delivery fee",
  PENALTY: "Penalty",
  SURCHARGE: "Surcharge",
  OTHER: "Other",
}

// Display text for an EXTRA row (fees, penalties… paid on top of the PO lines).
export const extraItemName = (extraType, description) =>
  `Extra · ${EXTRA_TYPE_LABELS[extraType] || extraType || "Other"}${description ? ` — ${description}` : ""}`

// GET /payment-requests/{id}/budget-allocations row. BE has no department
// name, so it is resolved from `departmentNames` (id → name). EXTRA rows carry
// no OR line / item / budget, only the amount.
export const fromAllocationRow = (r, departmentNames = new Map()) => ({
  ...fromMatrixRow({}),
  key: `${
    r.paymentRequestLineId != null
      ? `line:${r.paymentRequestLineId}`
      : `${r.purchaseId}:${idList(r.orLineIds).join("+") || r.orLineId || "po"}`
  }${r.periodKey ? `:${r.periodKey}` : ""}`,
  purchaseId: String(r.purchaseId),
  poCode: r.poCode || "",
  orCode: r.orCode || "",
  orLineId: r.orLineId == null ? null : String(r.orLineId),
  lineType: r.lineType || "ITEM",
  extraType: r.extraType || "",
  description: r.description || "",
  allocationSource: r.allocationSource || "",
  month: num(r.fiscalMonth),
  year: num(r.fiscalYear),
  periodKey: r.periodKey || "",
  periodLabel: r.periodLabel || "",
  budgetStatus: r.budgetStatus || "",
  unallocatedMonths: r.unallocatedMonths || "",
  orLineIds: idList(r.orLineIds ?? r.orLineId),
  centerId: r.centerId,
  sunCenterCode: r.sunCenterCode || "",
  centerName: r.centerName || "",
  budgetCenterId: r.budgetCenterId,
  budgetCenterName: r.budgetCenterName || "",
  budgetCategoryId: r.budgetCategoryId,
  departmentName: departmentNames.get(String(r.departmentId)) || "",
  categoryName: r.categoryName || "",
  itemGroupCode: r.itemGroupCode || "",
  itemGroupName: r.itemGroupName || "",
  itemName: r.lineType === "EXTRA" ? extraItemName(r.extraType, r.description) : r.itemName || "",
  quantity: num(r.quantity),
  accountCode: r.accountCode || "",
  ...Object.fromEntries(LEVELS.map((n) => [`l${n}Code`, r[`l${n}Code`] || ""])),
  orLineAmount: num(r.orLineAmount),
  poLineAmount: num(r.poLineAmount),
  value: num(r.value),
  lineUnpaid: num(r.lineUnpaid),
  linePaid: num(r.linePaid),
  budget: num(r.budget),
  remaining: num(r.remaining),
  remainingAfter: num(r.remainingAfter),
  budgetImpact: r.budgetImpact || "",
  poPaymentAmount: num(r.poPaymentAmount),
  poTotalAmount: num(r.poTotalAmount),
  poPaidAmount: num(r.poPaidAmount),
  poPaymentPercentage: num(r.poPaymentPercentage),
  poPaidPercentage: num(r.poPaidPercentage),
  validationStatus: r.validationStatus === "OK" || r.validationStatus === "EXTRA" ? "" : r.validationStatus || "",
})

// A Non-PO has no PO/OR rows until it is saved: its lines take their budget
// figures from the non-PO budget preview of their center (`previewByCenter`),
// whose `remainingAfterPreview` already nets every line of that center.
// Off-catalog items have no account / analysis codes.
export const fromDirectLines = ({ lines, previewByCenter, departmentName, month, year }) =>
  lines.map((line) => {
    const p = previewByCenter.get(String(line.centerId)) || {}
    return {
      ...fromMatrixRow({}),
      key: line.key,
      purchaseId: "",
      month,
      year,
      sunCenterCode: p.sunCenterCode || "",
      centerName: p.centerName || "",
      budgetCenterName: p.budgetCenterName || "",
      departmentName: departmentName || "",
      categoryName: p.categoryName || "",
      itemName: line.isCustomItem ? line.customItemName : line.itemName,
      quantity: 1,
      poLineAmount: num(line.amount),
      value: num(line.amount),
      lineUnpaid: null,
      linePaid: null,
      budget: num(p.budget),
      remaining: num(p.remaining),
      remainingAfter: num(p.remainingAfterPreview),
      budgetImpact: "PREVIEW",
      validationStatus: p.checkStatus && p.checkStatus !== "BUDGET_FOUND" ? p.checkStatus : "",
    }
  })

// Last share takes the rounding remainder so the parts add up to `total`.
export const splitByWeight = (total, weights) => {
  const sum = weights.reduce((s, w) => s + w, 0)
  const ws = sum > 0 ? weights : weights.map(() => 1)
  const wsum = sum > 0 ? sum : weights.length
  let used = 0
  return ws.map((w, i) => {
    if (i === ws.length - 1) return total - used
    const part = Math.round((total * w) / wsum)
    used += part
    return part
  })
}

const pct = (part, total) => (part != null && total > 0 ? Math.round((part / total) * 10000) / 100 : null)

// Create-form preview: each line gets the PO's payment amount split by PO
// line amount (BE's weight too), plus the PO-level payment figures the form already knows
// (`poTotalByPurchase`). Paid-to-date isn't known before saving, so it stays empty.
export const fillValues = (rows, amountByPurchase, poTotalByPurchase = {}) => {
  const defaults = {}
  const byPo = new Map()
  rows.forEach((r) => byPo.set(r.purchaseId, [...(byPo.get(r.purchaseId) || []), r]))
  byPo.forEach((group, purchaseId) => {
    const amount = num(amountByPurchase[purchaseId])
    if (amount == null) return
    splitByWeight(amount, group.map((r) => r.poLineAmount || 0)).forEach((v, i) => {
      defaults[group[i].key] = v
    })
  })
  return rows.map((r) => {
    const poPaymentAmount = num(amountByPurchase[r.purchaseId])
    const poTotalAmount = num(poTotalByPurchase[r.purchaseId])
    return {
      ...r,
      value: r.value ?? defaults[r.key] ?? null,
      poPaymentAmount,
      poTotalAmount,
      poPaymentPercentage: pct(poPaymentAmount, poTotalAmount),
    }
  })
}

// Create/edit form: overlays the typed per-line values (`states`, purchaseId →
// lineAllocation state) on the preview rows and appends each PO's extra rows.
// POs without a state yet keep the proportional preview.
export const withLineValues = (rows, states) => {
  const out = []
  rows.forEach((r, i) => {
    const state = states[r.purchaseId]
    const line = state?.lines.find((l) => l.orLineId === r.orLineId)
    out.push(
      line
        ? {
            ...r,
            value: line.value,
            maxValue: line.remaining,
            poLineAmount: line.lineValue,
            paidByOthers: line.paidByOthers,
            pendingByOthers: line.pendingByOthers,
            linePaid: line.linePaid,
            lineUnpaid: line.lineUnpaid,
          }
        : r
    )
    if (state && rows[i + 1]?.purchaseId !== r.purchaseId) {
      state.extras.forEach((e) =>
        out.push({
          ...fromMatrixRow({}),
          key: e.key,
          purchaseId: r.purchaseId,
          poCode: r.poCode,
          lineType: "EXTRA",
          extraType: e.extraType,
          description: e.description,
          itemName: extraItemName(e.extraType, e.description),
          value: e.amount,
          poPaymentAmount: r.poPaymentAmount,
          poTotalAmount: r.poTotalAmount,
          poPaymentPercentage: r.poPaymentPercentage,
        })
      )
    }
  })
  return out
}

// Consecutive rows of the same PO, for the per-PO header row in the table.
export const groupByPo = (rows) =>
  rows.reduce((groups, r) => {
    const last = groups[groups.length - 1]
    if (last && last.purchaseId === r.purchaseId) last.rows.push(r)
    else groups.push({ purchaseId: r.purchaseId, poCode: r.poCode, head: r, rows: [r] })
    return groups
  }, [])

export const sumBy = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0)

// Rows of the "Chưa phân phối" period collapse into one row per PO × center, whatever the
// item. Funded months stay one row per PO × center × item × month. A merged row keeps its
// lines in `groupLines` so a typed payment can be split back by poLineAmount.
const MERGE_SUM = ["value", "maxValue", "poLineAmount", "orLineAmount", "linePaid", "lineUnpaid"]
export const mergePeriodRows = (rows) => {
  const out = []
  const byId = new Map()
  rows.forEach((r) => {
    if (r.lineType !== "ITEM" || !r.periodKey) {
      out.push(r)
      return
    }
    const unallocated = r.periodKey === UNALLOCATED
    const id = [r.purchaseId, r.centerId, unallocated ? "" : r.itemId, r.periodKey].join("|")
    const prev = byId.get(id)
    const entry = { orLineId: r.orLineId, weight: r.poLineAmount || 0 }
    if (!prev) {
      const row = { ...r, groupLines: [entry], itemNames: r.itemName ? [r.itemName] : [] }
      byId.set(id, row)
      out.push(row)
      return
    }
    prev.groupLines.push(entry)
    prev.key = `${prev.key}+${r.orLineId ?? r.key}`
    if (r.itemName && !prev.itemNames.includes(r.itemName)) prev.itemNames.push(r.itemName)
    prev.unallocatedMonths = [...new Set(`${prev.unallocatedMonths}, ${r.unallocatedMonths}`.split(",").map((m) => m.trim()).filter(Boolean))].join(", ")
    prev.orLineIds = [...new Set([...(prev.orLineIds || []), ...(r.orLineIds || [])])]
    MERGE_SUM.forEach((k) => {
      if (prev[k] != null || r[k] != null) prev[k] = (Number(prev[k]) || 0) + (Number(r[k]) || 0)
    })
  })
  return out.map((r) =>
    r.groupLines?.length > 1
      ? { ...r, itemName: r.itemNames.length > 1 ? `${r.itemNames.length} items` : r.itemNames[0] || "" }
      : { ...r, groupLines: undefined, itemNames: undefined }
  )
}


// exceljs is large, so it is only pulled in when someone actually downloads.
export const downloadBudgetAllocationXlsx = async (rows, fileBase) => {
  const { default: ExcelJS } = await import("exceljs")
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet("Budget allocation", { views: [{ state: "frozen", ySplit: 1 }] })
  ws.columns = EXCEL_COLUMNS.map((c) => ({
    header: c.label,
    key: c.key,
    width: c.width,
    style: c.money ? { numFmt: "#,##0" } : c.percent ? { numFmt: "0.##" } : {},
  }))
  rows.forEach((r) =>
    ws.addRow(r.periodKey === UNALLOCATED ? { ...r, month: r.periodLabel, year: null } : r)
  )

  const total = ws.addRow({
    month: "Total",
    orLineAmount: sumBy(rows, "orLineAmount"),
    poLineAmount: sumBy(rows, "poLineAmount"),
    value: sumBy(rows, "value"),
  })
  total.font = { bold: true }
  const header = ws.getRow(1)
  header.font = { bold: true }
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } }
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: EXCEL_COLUMNS.length } }

  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "")
  link.href = url
  link.download = `${fileBase}-budget-allocation-${stamp}.xlsx`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
